import { PDPViews, TAsset, TAssetGroup } from '@types';
import React, { ComponentProps, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import ProductInfoContainer from '../ProductInfoContainer';
import BackHeader from './BackHeader';
import { setView, setActiveAsset } from '@/actions/pdp.actions';
import usePartnerAssetGroups from '@features/partner/hooks/usePartnerAssetGroups';
import AssetGroup from '@components/tailwind/AssetGroup';
import Asset from '@components/tailwind/Asset';
import DesignGridWrapper from '../DesignGridWrapper';
import { Button } from '@components/tailwind';
import useAsset from '@features/partner/hooks/useAsset';
import ScrollTo from '@/components/tailwind/ScrollTo';
import { PreviewRenderCanvas } from '@c2s-npm/plumreact';

const PartnerDesignsView = () => {
  const dispatch = useAppDispatch();
  const {
    miyo,
    view,
    details: {
      attributes: { all: allAttributes },
    },
  } = useSelector((state: any) => state.pdpItem);
  const { asset, setAsset } = useAsset();
  const { activeGroup, selectGroup } = usePartnerAssetGroups({
    // activeAsset: miyo?.asset,
    activeAsset: null,
    assetGroups: miyo?.partnerConfig?.assetGroups ?? [],
    assets: miyo?.partnerConfig?.assets ?? [],
  });
  const [selectedAsset, setSelectedAsset] = useState<TAsset | null>(miyo?.asset);

  const [singleGroup, setSingleGroup] = useState(false);
  useEffect(() => {
    if (!activeGroup?.assetGroup && !activeGroup?.prevAssetGroup && activeGroup?.groups?.length === 1) {
      if (!singleGroup) {
        selectGroup(activeGroup.groups[0])();
        setSingleGroup(true);
      } else {
        dispatch(setView(PDPViews.DEFAULT));
      }
    }
  }, [activeGroup, singleGroup, selectedAsset]);

  if (view !== PDPViews.ASSET_PREVIEW) return null;

  const selectAssetHandler = (asset: TAsset) => () => {
    setSelectedAsset(asset);
  };

  const selectGroupHandler = (group: TAssetGroup) => () => {
    selectGroup(group)();
  };

  const onUpdateClick = () => {
    dispatch(setActiveAsset(selectedAsset));
    setAsset(selectedAsset);
    dispatch(setView(PDPViews.DEFAULT));
  };

  const onCancelClick = () => {
    setSelectedAsset(asset);
    dispatch(setView(PDPViews.DEFAULT));
  };

  const getBackText = (): ComponentProps<typeof BackHeader> => {
    const defaultReturn = {
      text: (
        <>
          Back to{' '}
          <span className='tw:font-bold'>{allAttributes.find((attr) => attr.key === 'type').value}</span>
        </>
      ),
      image: null,
      onClick: () => dispatch(setView(PDPViews.DEFAULT)),
    };

    if (!activeGroup.assetGroup && !activeGroup.prevAssetGroup) return defaultReturn;

    if (activeGroup.assetGroup && !activeGroup.prevAssetGroup)
      return {
        text: <>Design Gallery</>,
        image: miyo?.partnerConfig?.partner?.galleryUrl,
        onClick: () => selectGroup(null)(),
      };

    if (activeGroup.prevAssetGroup) {
      return {
        text: <>{activeGroup.prevAssetGroup.groupName}</>,
        image: activeGroup.prevAssetGroup.previewUrl,
        onClick: () => selectGroup(activeGroup.prevAssetGroup)(),
      };
    }

    return defaultReturn;
  };

  return (
    <div className='tw:py-10 container'>
      <ScrollTo />
      <div className='row'>
        <div className='col-lg-7'>
          <div className='tw:p-5 tw:bg-white tw:rounded-xl'>
            <PreviewRenderCanvas
              config={miyo.preview[0]}
              designUrl={selectedAsset.fileUrl}
              graphicType={'engraved'}
              size={'large'}
              zoom={1}
            />
          </div>
        </div>
        <div className='col-lg-5 product-details'>
          <ProductInfoContainer className='tw:!px-0'>
            <BackHeader {...getBackText()} />
            <div className='tw:px-4 tw:py-6 tw:bg-white tw:rounded-xl'>
              {activeGroup.assetGroup && (
                <p className='tw:mb-7 tw:text-center tw:text-lg tw:font-bold'>
                  {activeGroup.assetGroup.groupName}
                </p>
              )}

              <DesignGridWrapper maxHeight='400px'>
                {activeGroup.groups.length > 0 && (
                  <div className='w-justify-center tw:mb-5 tw:flex tw:flex-col tw:gap-4 tw:md:justify-start'>
                    {activeGroup.groups.map((group) => (
                      <AssetGroup
                        key={group.id}
                        text={group.groupName}
                        imageUrl={group.previewUrl}
                        onClick={selectGroupHandler(group)}
                      />
                    ))}
                  </div>
                )}

                {activeGroup.assets.length > 0 && (
                  <div className='tw:mb-5 tw:flex tw:flex-wrap tw:justify-center tw:gap-4'>
                    {activeGroup.assets.map((asset) => (
                      <Asset
                        size='95px'
                        key={asset.id}
                        asset={asset}
                        onClick={selectAssetHandler(asset)}
                        selected={asset.id === selectedAsset.id}
                      />
                    ))}
                  </div>
                )}
              </DesignGridWrapper>

              {activeGroup.assets.length > 0 && (
                <div className='tw:mt-5 tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-5'>
                  <Button
                    disabled={asset?.id === selectedAsset.id}
                    variant='primary'
                    size='lg'
                    onClick={onUpdateClick}>
                    Update Artwork
                  </Button>
                </div>
              )}
              <div className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-5'>
                <Button variant='link' size='base' onClick={onCancelClick}>
                  cancel
                </Button>
              </div>
            </div>
          </ProductInfoContainer>
        </div>
      </div>
    </div>
  );
};

export default PartnerDesignsView;
